import React from 'react';
import { IResizeEntry, ResizeSensor, Button } from '@blueprintjs/core';
import cloneDeep from 'lodash.clonedeep';
import i18n from 'i18n';
import { getSortedFormTemplateLayout, TARGETS_DEFAULT_LAYOUT_ORDER } from 'utils/formTemplate/getSortedFormTemplateLayout';
import { KeyValuePair } from 'data/types';
import { TargetModel } from 'data/target/target.types';
import { TargetDomain } from 'data/targetDomain/targetDomain.types';
import TargetOptionsDialog from 'xcipio/cases/TargetOptions';
import { TargetOptions } from 'data/case/case.types';
import DynamicForm, { ChangedField } from '../../../shared/form';
import './targets.scss';

const getOptionValue = (key: string, options: { [key: string]: string }[]): string =>
  options.find((option) => option.key === key)?.value ?? '';

const getTargetOptionData = (target: any) => {
  let targetOptions = {};
  const genericTargets = filterGenericTargetOptions(target.options);
  if (genericTargets.length > 0) {
    targetOptions = cloneDeep(getTargetOptionsListToObject(genericTargets));
  }

  if (target.primaryType === 'VOLTE') {
    return {
      ...targetOptions,
      WARRANT_HOSTS: getOptionValue('WARRANT_HOSTS', target.options),
    };
  }

  const option = target?.options?.find((opt: {key: string, value: string }) => opt.key === 'LOC_POLL_FREQ');
  if (option == null) {
    return {
      ...targetOptions,
      LOC_POLL_FREQ: 0,
    };
  }

  return {
    ...targetOptions,
    LOC_POLL_FREQ: option.value,
  };
};

// Will use this approach to avoid introduce regretion issues to the existing target options support
// but, all target options must be set via ontoogy file as soon as possible
const CurrentSupportedTargetOptions = {
  tid: true,
  coid: true,
  WARRANT_HOSTS: true,
  LOC_POLL_FREQ: true,
}
const getGenericTargetOptions = (targetOptions: TargetOptions) => {
  return Object.keys(targetOptions)
    .filter((optionKey: string) => !(CurrentSupportedTargetOptions as any)[optionKey]);
}
const filterGenericTargetOptions = (options: KeyValuePair[]) => {
  return options.filter((option: any) => !(CurrentSupportedTargetOptions as any)[option.key]);
}

const getTargetOptionsObject = (targetOptions: TargetOptions, targetOptionsKeys: string[]) => {
  return targetOptionsKeys
  .map((key) => ({ [key]: targetOptions[key as keyof TargetOptions] }))
  .reduce((t, a) => ({ ...t, ...a }), {});
}

const getTargetOptionsListToObject = (targetOptions: KeyValuePair[]) => {
  return targetOptions.reduce((total, actual) => ({
    ...total,
    [actual.key]: actual.value
  }), {});
}

export default class TargetDetails extends React.Component<any, any> {
  targetRef : any

  constructor(props:any) {
    super(props);
    this.state = {
      targetsRowContainerSize: {},
      fields: props.targetField.subFields,
      isTargetOptionsVisible: false,
    };
  }

  customModifyTargetInputByType = (target: any) => {
    if (!target) return;
    target = cloneDeep(target);

    if (target.primaryType === 'VZID') {
      target.optionValue = target.options?.find((option: KeyValuePair) => option.key === 'PCRFUsername')?.value;
    }

    target.options = target.options?.[0]?.value ?? '';

    return target;
  }

  isTargetValid = (forceTouched = true) => {
    if (this.targetRef) {
      return this.targetRef.isValid(true, forceTouched);
    }
    return true;
  }

  targetUpdateHandler = (changedField: ChangedField): void => {
    const { value, name } = changedField;
    const { target, fieldUpdateHandler, warrantConfig } = this.props;
    const { fields } = this.state;

    if (target.primaryType === 'VZID' && name === 'secondaryType') {
      const newFields = cloneDeep(fields);

      if (changedField.value === '') {
        newFields.secondaryValue.validation.required = false;
        newFields.secondaryValue.validation.validators = undefined;
      } else {
        newFields.secondaryValue.validation.validators = warrantConfig.getTargetValidation(
          newFields,
          'secondaryValue',
          'regexp',
          changedField.value,
          'pattern',
        );
      }

      this.setState({ fields: newFields });
    }

    this.setState({ target: { ...this.state.target, [name]: value } });
    fieldUpdateHandler(changedField);
  }

  handleOpenTargetOptions = (): void => {
    this.setState({
      isTargetOptionsVisible: true,
    });
  }

  handleTargetOptionsClose = (): void => {
    this.setState({
      isTargetOptionsVisible: false,
    });
  }

  handleUpdateTargetOptions = (targetOptions: TargetOptions): void => {
    const { target, fieldUpdateHandler, targetDomainList } = this.props;
    let options;
    const genericTargetOptionsKeys = getGenericTargetOptions(targetOptions);
    if (genericTargetOptionsKeys.length > 0) {
       options = getTargetOptionsObject(targetOptions, genericTargetOptionsKeys);
    }

    // will update targets LOC_POLL_FREQ only in case LOC_POLL_FREQ is enable
    if (this.shoudConfigPullLocationUpdates(target) && target?.options?.length > 0) {
      target?.options?.forEach((opt: {key: string, value: string }) => {
        if (opt.key === 'LOC_POLL_FREQ' && targetOptions.LOC_POLL_FREQ != null) {
          opt.value = targetOptions.LOC_POLL_FREQ;
        }
      });

      const updateOptions = options == null ?
        targetOptions.LOC_POLL_FREQ :
        { ...options, LOC_POLL_FREQ: targetOptions.LOC_POLL_FREQ };

      fieldUpdateHandler(
        {
          name: 'options',
          value: updateOptions,
        },
        true,
      );
    }
    // will update targets only in case there are targets domain options
    if (this.shoudConfigTargetDomainList(target, targetDomainList)) {
      fieldUpdateHandler(
        {
          name: 'options',
          value: targetOptions,
        },
        true,
      );

      target.options = cloneDeep([targetOptions]);
      this.setState({
        formName: Date.now().toString(),
      });
    }

    // will update targets only in case there are targets domain options
    if (this.shouldConfigTargetOptions(target.primaryType)) {
      fieldUpdateHandler(
        {
          name: 'options',
          value: targetOptions,
        },
        true,
      );

      target.options = cloneDeep([targetOptions]);
      this.setState({
        formName: Date.now().toString(),
      });
    }

    if (!this.shoudConfigPullLocationUpdates(target) && genericTargetOptionsKeys.length > 0) {
      const options = getTargetOptionsObject(targetOptions, genericTargetOptionsKeys);
        fieldUpdateHandler(
          {
            name: 'options',
            value: options,
          },
          true,
        );
    }
  }

  /**
   * target field needs to know about its parent's size so the entry field can take up the entire
   * width of its parent if the entry could be long
   */
  handleResize = (entries: IResizeEntry[]) => {
    if (entries && entries.length > 0) {
      const targetsRowContainerSize = {
        width: entries[0].contentRect.width,
        height: entries[0].contentRect.height,
      };
      this.setState({ targetsRowContainerSize });
    }
  }

  shoudConfigPullLocationUpdates = (target: TargetModel): boolean => {
    return (!!target?.options?.find((opt) => opt.key === 'LOC_POLL_FREQ'));
  }

  shoudConfigTargetDomainList = (target: TargetModel, targetDomainList: TargetDomain[]): boolean => {
    return (targetDomainList?.length > 0 && (
      target?.actualPrimaryType === 'SIP-URI' ||
      target?.primaryType === 'SIP-URI' ||
      target?.actualPrimaryType === 'SIP-URI-CUSTOM' ||
      target?.primaryType === 'SIP-URI-CUSTOM'
    ));
  }

  shouldConfigTargetOptions = (primaryType?:string) => {
    return ['VOLTE'].includes(primaryType || '');
  }

  showOptionsButton = (target: TargetModel, targetDomainList: TargetDomain[]): boolean => {
    const { allowedTargetsToSetOptions } = this.props;
    const primaryTargetType = target.actualPrimaryType ?? target.primaryType;
    if (allowedTargetsToSetOptions?.includes(primaryTargetType)) {
      return true;
    }

    return this.shouldConfigTargetOptions(target.primaryType) || this.shoudConfigPullLocationUpdates(target) || this.shoudConfigTargetDomainList(target, targetDomainList);
  }

  render(): JSX.Element {
    const {
      targetField, formId, target, targetDomainList, mode, allowedTargetsToSetOptions
    } = this.props;

    const {
      fields, targetsRowContainerSize, isTargetOptionsVisible, formName,
    } = this.state;

    return (
      <div style={{ display: 'flex' }}>
        <ResizeSensor onResize={this.handleResize}>
          <span className="targetsRowContainer" data-testid="TargetDetails">
            {targetField.title !== '' ? `${targetField.title}: ` : ''}
            <div className="targetsFormRowContainer">
              <div className="targetsFormRowContainerForm formDisabled">
                <DynamicForm
                  name={formName || formId}
                  ref={(ref) => { this.targetRef = ref; }}
                  fields={fields}
                  layout={getSortedFormTemplateLayout(fields, TARGETS_DEFAULT_LAYOUT_ORDER)}
                  defaults={this.customModifyTargetInputByType(target)}
                  fieldUpdateHandler={this.targetUpdateHandler}
                  parentSize={targetsRowContainerSize}
                  localizationDisabled
                />
              </div>
              {this.showOptionsButton(target, targetDomainList) && (
                <div className="targetsRowContainerTargetOptions">
                  <Button type="button" onClick={this.handleOpenTargetOptions}>
                    {i18n.t('common.button.options')}
                  </Button>
                </div>
              )}
            </div>
          </span>
        </ResizeSensor>
        <TargetOptionsDialog
          title={i18n.t('warrant.target.titleEditTargetOptions')}
          target={target}
          isOpen={isTargetOptionsVisible}
          data={getTargetOptionData(target)}
          targetDomainList={targetDomainList}
          shoudConfigPullLocationUpdates={this.shoudConfigPullLocationUpdates(cloneDeep(target))}
          shoudConfigTargetDomainList={this.shoudConfigTargetDomainList(target, targetDomainList)}
          shouldConfigTargetOptions={this.shouldConfigTargetOptions(target.primaryType)}
          allowedTargetsToSetOptions={allowedTargetsToSetOptions}
          onClose={this.handleTargetOptionsClose}
          onSubmit={this.handleUpdateTargetOptions}
          currentStateMode={mode}
        />
      </div>

    );
  }
}
